import React from 'react'

import clsx from 'clsx'

import ToDoCheckbox from '$extensionSrc/components/ToDoCheckbox'
import CheckmarkDoneIcon from '$extensionSrc/icons/checkmark-done-icon.svg'
import CheckMarkIcon from '$extensionSrc/icons/checkmark-icon-small.svg'
import CheckmarkUpdatedDoneIcon from '$extensionSrc/icons/checkmark-updated-done-icon.svg'
import IncompleteIcon from '$extensionSrc/icons/incomplete-icon.svg'
import { RecommendationState } from '$extensionSrc/utils/enums'

import styles from './SettingIcon.module.scss'

/**
 * Convenience wrapper to standardize some attributes
 */
function Wrapper({
  children,
  className,
  labelId,
}: React.PropsWithChildren<{ className: string; labelId: string }>) {
  return (
    <div role="img" className={className} aria-labelledby={labelId}>
      {children}
    </div>
  )
}

export default function SettingIcon({
  disabled,
  fulfilled,
  labelId,
  lessRelevant,
  locked,
  onSelectSetting,
  recommendationState,
  selected,
}: {
  disabled: boolean
  fulfilled: boolean
  labelId: string
  lessRelevant: boolean
  locked: boolean
  onSelectSetting: () => void
  recommendationState: RecommendationState
  selected: boolean
}) {
  const fulfilledIcon = lessRelevant ? (
    <Wrapper className="SettingIcon" labelId={labelId}>
      <CheckMarkIcon
        className={clsx(styles.icon, styles.lessRelevant)}
        aria-hidden
      />
    </Wrapper>
  ) : (
    <Wrapper className="SettingIcon" labelId={labelId}>
      <CheckmarkDoneIcon className={styles.icon} aria-hidden />
    </Wrapper>
  )

  const incompleteIcon = (
    <Wrapper className="SettingIcon" labelId={labelId}>
      <IncompleteIcon className={styles.icon} aria-hidden />
    </Wrapper>
  )

  if (locked) {
    if (fulfilled) {
      return fulfilledIcon
    }
    return (
      <ToDoCheckbox
        disabled
        isSettingSelected={selected}
        onClick={onSelectSetting}
        labelId={labelId}
      />
    )
  }

  if (recommendationState === RecommendationState.INCOMPLETE) {
    if (fulfilled) {
      return fulfilledIcon
    }
    return (
      <ToDoCheckbox
        disabled={disabled}
        isSettingSelected={selected && !disabled}
        onClick={onSelectSetting}
        labelId={labelId}
      />
    )
  } else if (recommendationState === RecommendationState.FULFILLED) {
    if (fulfilled) {
      return fulfilledIcon
    }

    if (disabled) {
      return incompleteIcon
    }

    return selected ? fulfilledIcon : incompleteIcon
  } else if (recommendationState === RecommendationState.SKIPPED) {
    return incompleteIcon
  } else if (recommendationState === RecommendationState.UPDATED) {
    if (fulfilled) {
      return fulfilledIcon
    }

    if (disabled) {
      return incompleteIcon
    }

    if (selected) {
      if (lessRelevant) {
        return (
          <Wrapper className="SettingIcon" labelId={labelId}>
            <CheckMarkIcon
              className={clsx(styles.icon, styles.lessRelevant)}
              aria-hidden
            />
          </Wrapper>
        )
      }
      return (
        <Wrapper className="SettingIcon" labelId={labelId}>
          <CheckmarkUpdatedDoneIcon className={clsx(styles.icon)} aria-hidden />
        </Wrapper>
      )
    }
    return incompleteIcon
  }
  // We shouldn't reach here, but if we do, return the checkbox
  return (
    <ToDoCheckbox
      disabled={disabled}
      isSettingSelected={selected && !disabled}
      onClick={onSelectSetting}
      labelId={labelId}
    />
  )
}
